import { useState, useEffect } from "react";
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from "@dnd-kit/core";
import { restrictToVerticalAxis } from "@dnd-kit/modifiers";
import {
  arrayMove,
  verticalListSortingStrategy,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { Flex } from "antd";
import ChecklistItem from "../ChecklistItem";

const Checklist = ({ data, readOnly, handleEditItem, handleDeleteItem }) => {
  const [list, setList] = useState(data || []);

  useEffect(() => {
    setList(data || []);
  }, [data]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, {
      activationConstraint: {
        delay: 150,
        tolerance: 8,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    }),
  );

  const handleDragEnd = ({ active, over }) => {
    if (over && active.id !== over?.id) {
      const activeItem = list.find(({ uuid }) => uuid === active.id);
      const activeIndex = list.findIndex(({ uuid }) => uuid === active.id);
      const overIndex = list.findIndex(({ uuid }) => uuid === over.id);

      const newData = arrayMove(list, activeIndex, overIndex);
      setList([...newData]);

      handleEditItem({ ...activeItem, idx: overIndex });
    }
  };

  return (
    <DndContext
      sensors={sensors}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
      modifiers={[restrictToVerticalAxis]}
    >
      <SortableContext
        items={list.map((item) => item.uuid)}
        strategy={verticalListSortingStrategy}
      >
        <Flex vertical gap={5}>
          {list.map((item) => (
            <SortableItem
              key={item.uuid}
              id={item.uuid}
              item={item}
              readOnly={readOnly}
              handleEditItem={handleEditItem}
              handleDeleteItem={handleDeleteItem}
            />
          ))}
        </Flex>
      </SortableContext>
    </DndContext>
  );
};

function SortableItem(props) {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: props.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  return (
    <ChecklistItem
      ref={setNodeRef}
      item={props.item}
      onSave={props.handleEditItem}
      onDelete={props.handleDeleteItem}
      readOnly={props.readOnly}
      style={style}
      dragListeners={listeners}
      dragAttributes={attributes}
    />
  );
}

export default Checklist;
